import Link from 'next/link';
import Image from 'next/image';

import Layout from '@/components/Layout/Vanilla';
import Button from '@/components/Button';
import { DefaultLogoReel } from '@/components/LogoReel';
import USPCardRedesigned from '@/components/USPCardRedesigned';
import CaseStudyTabs from '@/components/CaseStudyTabs';
import { fetchPage, gql } from '@/lib/graphcms';

const enablingYouToBuildYourBest = [
  {
    image: {
      url: '/images/for-businesses/localized-content-royal-blue.svg',
      width: 259,
      height: 195,
    },
    title: 'Localized Content',
    description:
      'Whether you’re handling corporate presence in multiple regions or delivering locale-specific products, our out-of-the-box localization sets you up for success.',
    ctaTo: '/use-cases/content-localization',
  },
  {
    image: {
      url: '/images/for-businesses/structured-content-royal-blue.svg',
      width: 336,
      height: 231,
    },
    title: 'Structured Content',
    description:
      'Easily create, iterate on, and enrich your content. Further enhance it through third-party APIs, and unlock programmatic approaches to managing and delivering content.',
    ctaTo: '/use-cases/structured-content',
  },
  {
    image: {
      url: '/images/for-businesses/content-federation-royal-blue.svg',
      width: 255,
      height: 203,
    },
    title: 'Content Federation',
    description:
      'Break down your content silos. GraphCMS provides you with central control of your enterprise content from multiple sources through a single, unified content layer.',
    ctaTo: '/use-cases/content-federation',
  },
];

const headlessEnterpriseContent = [
  {
    icon: 'DatabaseLineIcon',
    title: 'End-to-end managed service',
    description:
      'GraphCMS hosts your content in data centers of your choice, with your content cached across 190 edge CDN nodes globally. We provide custom support and performance SLAs with 24/7 infrastructure monitoring.',
  },
  {
    icon: 'ShieldUserLineIcon',
    title: 'Security & governance',
    description:
      'Get granular content based permissions, audit logs, backups, and content retention to ensure you never lose business-critical information.',
  },
  {
    icon: 'TerminalLineIcon',
    title: 'Market leading APIs',
    description:
      'GraphCMS gives you all your content from a single endpoint via the industry’s best APIs, giving you full access to our Management API, Content API, Assets API, and GraphQL mutations via our Mutations API.',
  },
  {
    icon: 'BubbleChartFillIcon',
    title: 'Agile content management',
    description:
      'Use agile practices to minimize disruption to your customers, you need to carefully navigate your way through delivering new digital experiences with optimized workflows, reduced overheads, and minimal team-dependencies.',
  },
  {
    icon: 'TimerFlashLineIcon',
    title: 'Battle tested at every scale',
    description:
      'With industry leading performance, security, and scalability, GraphCMS is trusted by companies ranging from the Fortune 500 to governments and public service companies.',
  },
  {
    icon: 'ShieldCheckLineIcon',
    title: 'Enterprise grade compliance',
    description:
      'All our tools and services are compliant with, and certified against the strictest regulations. For further control, host your content on a dedicated infrastructure.',
  },
];

export default function ForBusinesses({ page, preview }) {
  const { usps, caseStudies } = page;

  return (
    <Layout seo={page} preview={preview}>
      <section className="py-6">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="flex flex-col space-y-12 md:flex-row md:items-center md:space-x-6 md:space-y-0">
            <div className="w-full space-y-6 md:w-2/5">
              <p className="font-semibold uppercase tracking-wider text-royal-blue">
                For businesses
              </p>
              <h1 className="text-3xl font-bold leading-tight md:text-5xl">
                {page.title}
              </h1>
              <p className="leading-relaxed text-zodiac md:text-lg">
                {page.subTitle}
              </p>
              <Button href="https://graphcms.com/contact" colour="royal">
                Get enterprise demo &#8594;
              </Button>
            </div>

            <div className="w-full md:w-3/5">
              <div className="relative md:-mr-32">
                <Image
                  src="/images/for-businesses/graphcms-for-enterprise-and-businesses.svg"
                  alt="for enterprise and small businesses"
                  width={747}
                  height={493}
                />
              </div>
            </div>
          </div>

          <div className="grid gap-x-6 pt-12 md:grid-cols-2 md:pb-6 lg:grid-cols-3 lg:pt-20">
            {usps.map((usp) => (
              <USPCardRedesigned key={usp.id} {...usp} theme="royal" />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <DefaultLogoReel />
        </div>
      </section>

      <section className="bg-white py-20 md:py-32">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="space-y-12 md:flex md:items-center md:space-y-0">
            <div className="w-full md:w-1/2 md:pr-12">
              <div className="relative inline-flex items-center pb-4">
                <span className="text-sm font-medium uppercase tracking-widest text-royal-blue">
                  Deliver content at breakneck speeds
                </span>
              </div>
              <h3 className="mb-4 text-xl font-bold leading-snug text-haiti md:text-2xl">
                &quot;The time to market has drastically reduced from over 2
                weeks to 3-4 days.&quot;
              </h3>
              <p className="mb-6 text-light-haiti">
                The BioCentury team rely on GraphCMS to federate their content
                from their CRM, CMS, and eCommerce systems, using GraphCMS as a
                programmatic Content Hub. The team delivers high-velocity
                content to millions of consumers globally, ensuring that
                mission-critical medical information doesn’t get lost at the
                speed of IT.
              </p>
              <Link href="/case-studies/biocentury-case-study" prefetch={false}>
                <a className="group font-medium text-royal-blue">
                  Learn how BioCentury does it
                  <span className="ml-1 inline-block transform transition-transform duration-100 group-hover:translate-x-1">
                    &#8594;
                  </span>
                </a>
              </Link>
            </div>

            <div className="h-full w-full md:flex md:w-1/2 md:items-center md:justify-center">
              <Image
                src="/images/for-businesses/biocentury.png"
                alt="biocentury content federation"
                width={1292}
                height={904}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-haiti py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mx-auto w-full space-y-6 pb-6 text-center md:pb-12">
            <h2 className="text-3xl font-bold leading-tight text-white md:text-5xl">
              Unify All Your Investments
            </h2>
            <p className="text-whisper">
              Supercharge your team&apos;s content pipeline with endless
              extensibility.
            </p>
          </div>

          <div className="mx-auto hidden max-w-4xl md:block">
            <Image
              src="/images/for-businesses/full-content-pipeline-royal-blue.svg"
              alt="full content pipeline"
              className="block"
              width={940}
              height={522}
            />
          </div>

          <div className="md:hidden">
            <div className="space-y-3 py-6">
              <Image
                src="/images/for-businesses/content-sources-royal-blue.svg"
                className="mb-12 block"
                alt="content sources"
                width={328}
                height={290}
              />
              <p className="text-sm font-medium uppercase tracking-widest text-royal-blue">
                Sources
              </p>
              <h2 className="text-2xl font-bold leading-tight text-white">
                Create Your Content
              </h2>
              <p className="text-whisper">
                Within minutes, define everything about your content API.
                Structures, localizations, relations, powerful permissions and
                more…
              </p>
            </div>

            <div className="space-y-3 py-6">
              <Image
                src="/images/for-businesses/content-destinations-royal-blue.svg"
                className="mb-12 block"
                alt="omni-channel delivery"
                width={328}
                height={287}
              />
              <p className="text-sm font-medium uppercase tracking-widest text-royal-blue">
                Destination
              </p>
              <h2 className="text-2xl font-bold leading-tight text-white">
                Put Your Content to Use
              </h2>
              <p className="text-whisper">
                Programmatically deliver to and consume content from several
                frontends and platforms like websites, portals, mobile, smart
                devices, and more.
              </p>
            </div>
          </div>

          <div className="mx-auto max-w-3xl pt-6 md:pt-12 md:text-center lg:pt-24">
            <h3 className="mb-4 text-xl font-bold leading-snug text-white md:text-2xl">
              See How GraphCMS Transforms Your Content Management
            </h3>
            <p className="text-whisper">
              GraphCMS plans are flexibly suited to accommodate your growth.
              Reach out to our sales team for a demo to discuss your projects
              and complex needs.
            </p>
            <div className="pt-6">
              <Button href="/contact" colour="white">
                Get enterprise demo &#8594;
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mx-auto w-full space-y-6 pb-6 text-center md:pb-20">
            <h2 className="text-3xl font-bold leading-tight text-haiti md:text-5xl">
              Enabling Your Team to Build Their Best
            </h2>
            <div className="mx-auto max-w-3xl">
              <p className="text-zodiac">
                GraphCMS unlocks operational efficiency for your team - by
                enabling agile workflows for your content creators and
                developers, to bring your ambitious projects to market faster.
              </p>
            </div>
          </div>

          <div className="grid gap-x-6 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
            {enablingYouToBuildYourBest.map(
              ({ image, title, description, cta, ctaTo }) => (
                <div
                  key={ctaTo}
                  className="space-y-3 md:space-y-6 lg:space-y-12"
                >
                  <div className="flex items-center justify-center">
                    <Image
                      src={image.url}
                      alt={title}
                      width={image.width}
                      height={image.height}
                    />
                  </div>
                  <div className="space-y-3 py-3 md:space-y-4">
                    <h3 className="text-xl font-bold text-haiti">{title}</h3>
                    <p className="text-light-haiti">{description}</p>
                    {ctaTo && (
                      <div>
                        <Link href={ctaTo} prefetch={false}>
                          <a className="group font-medium text-royal-blue">
                            {cta || 'Learn more'}
                            <span className="ml-1 inline-block transform transition-transform duration-100 group-hover:translate-x-1">
                              &#8594;
                            </span>
                          </a>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              ),
            )}
          </div>

          <div className="flex justify-center pt-10 md:pt-12">
            <div className="w-full md:w-1/3 md:px-8">
              <Button
                href="/use-cases"
                appearance="outline"
                colour="royal"
                block
              >
                See all solutions &#8594;
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20">
        <div className="mx-auto max-w-6xl space-y-12 px-4 md:space-y-24 md:px-6">
          <div className="w-full space-y-6 text-center">
            <h2 className="text-3xl font-bold leading-tight text-haiti md:text-5xl">
              Here to Power Your Business Ambitions
            </h2>
            <div className="mx-auto max-w-3xl">
              <p className="text-zodiac">
                Whether you’re a high-growth digital company with scaling needs
                or an enterprise business with modernizing global operations,
                GraphCMS forms the core of your stack, unifying your
                technologies and processes.
              </p>
            </div>
          </div>

          <div className="flex flex-col-reverse md:flex-row md:items-center">
            <div className="w-full md:w-1/2 md:pr-20">
              <h3 className="mb-4 text-xl font-bold leading-snug text-haiti md:text-2xl">
                Enable your stack to mature with your business
              </h3>
              <p className="mb-6 text-zodiac">
                Stop hacky workarounds and compromising on your goals of
                delivering high-performance digital experiences because of
                inflexible web technologies.
              </p>
              <Link href="/contact" prefetch={false}>
                <a className="group font-medium text-royal-blue">
                  Talk to an expert to learn how
                  <span className="ml-1 inline-block transform transition-transform duration-100 group-hover:translate-x-1">
                    &#8594;
                  </span>
                </a>
              </Link>
            </div>

            <div className="h-full w-full pb-6 md:flex md:w-1/2 md:items-center md:justify-center md:pb-0">
              <Image
                src="/images/for-businesses/incoming-api-requests.svg"
                alt="incoming api requests"
                width={602}
                height={398}
              />
            </div>
          </div>

          <div className="md:flex md:items-center">
            <div className="h-full w-full pb-6 md:flex md:w-1/2 md:items-center md:justify-center md:pb-0">
              <Image
                src="/images/for-businesses/graphcms-pipeline.svg"
                alt="graphcms pipeline"
                width={515}
                height={348}
              />
            </div>

            <div className="w-full md:w-1/2 md:pl-20">
              <h3 className="mb-4 text-xl font-bold leading-snug text-haiti md:text-2xl">
                Modernize your business to embrace digital-first
              </h3>
              <p className="mb-6 text-zodiac">
                Break down your silos by federating your content. GraphCMS can
                Integrate all your existing backend services and business
                processes to deliver content from a unified content layer
              </p>
              <Link href="/use-cases/content-federation" prefetch={false}>
                <a className="group font-medium text-royal-blue">
                  Learn more about Content Federation
                  <span className="ml-1 inline-block transform transition-transform duration-100 group-hover:translate-x-1">
                    &#8594;
                  </span>
                </a>
              </Link>
            </div>
          </div>

          <div className="flex flex-col-reverse md:flex-row md:items-center">
            <div className="w-full md:w-1/2 md:pr-20">
              <h3 className="mb-4 text-xl font-bold leading-snug text-haiti md:text-2xl">
                Gain access to a global network of implementation & ecosystem
                partners
              </h3>
              <p className="mb-6 text-zodiac">
                Need extra firepower to accelerate your development process?
                GraphCMS supports your needs to deliver on on enterprise
                expectations with our certified global partner network.
              </p>
              <Link href="/partners" prefetch={false}>
                <a className="group font-medium text-royal-blue">
                  Explore our partner network
                  <span className="ml-1 inline-block transform transition-transform duration-100 group-hover:translate-x-1">
                    &#8594;
                  </span>
                </a>
              </Link>
            </div>

            <div className="h-full w-full pb-6 md:flex md:w-1/2 md:items-center md:justify-center md:pb-0">
              <Image
                src="/images/for-businesses/partner-cards.svg"
                alt="graphcms partners"
                width={644}
                height={444}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-haiti py-20 md:py-32">
        <div className="mx-auto max-w-6xl px-4 md:px-6 md:pt-12">
          <div className="md:flex md:items-center">
            <div className="w-full md:w-1/2 md:pr-20">
              <div className="relative inline-flex items-center pb-4">
                <span className="text-sm font-medium uppercase tracking-widest text-royal-blue">
                  The monolith is dying, and taking you with it
                </span>
              </div>
              <h3 className="mb-4 text-xl font-bold leading-snug text-white md:text-2xl">
                &quot;Today’s customers demand a personalized experience across
                all digital touchpoints...&quot;
              </h3>
              <p className="mb-6 text-whisper">
                We’ve spoken to the leaders of the industry, from companies like
                AWS, G2, VWO, Optimove, Commerce Layer, and more, to understand
                where digital experiences are headed, and how a Headless CMS
                forms the foundation of DXPs moving forward.
              </p>
              <Link href="/resources/dxp-report-2020" prefetch={false}>
                <a className="group font-medium text-royal-blue">
                  Download the report
                  <span className="ml-1 inline-block transform transition-transform duration-100 group-hover:translate-x-1">
                    &#8594;
                  </span>
                </a>
              </Link>
            </div>

            <div className="h-full w-full pt-6 md:flex md:w-1/2 md:items-center md:justify-center md:pt-0">
              <Image
                src="/images/for-businesses/amit-bivas.png"
                alt="amit bivas"
                width={1292}
                height={904}
              />
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 pt-12 md:text-center lg:pt-32">
          <h3 className="mb-4 text-xl font-bold leading-snug text-white md:text-2xl">
            See How GraphCMS Transforms Your Content Management
          </h3>
          <p className="text-whisper">
            GraphCMS plans are flexibly suited to accommodate your growth. Reach
            out to our sales team for a demo to discuss your projects and
            complex needs.
          </p>
          <div className="pt-6">
            <Button href="/contact" colour="white">
              Get enterprise demo &#8594;
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-whisper py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mx-auto w-full max-w-4xl space-y-6 text-center">
            <h2 className="text-3xl font-bold leading-tight text-haiti md:text-5xl">
              Headless Enterprise Content Management Trusted by Leaders
            </h2>
            <div className="mx-auto max-w-3xl">
              <p className="text-zodiac">
                GraphCMS is relied upon across the world - from high-growth
                startups to the Fortune 500, from cloud-native SaaS companies to
                governments and public services.
              </p>
            </div>
          </div>

          <div className="grid gap-x-6 pt-12 md:grid-cols-2 md:pb-6 lg:grid-cols-3 lg:pt-20">
            {headlessEnterpriseContent.map((usp, index) => (
              <USPCardRedesigned key={index} {...usp} theme="royal" />
            ))}
          </div>

          <div className="flex justify-center pt-10 md:pt-12">
            <div className="w-full md:w-1/3 md:px-8">
              <Button
                href="/use-cases"
                colour="royal"
                appearance="outline"
                block
              >
                Explore GraphCMS for enterprise &#8594;
              </Button>
            </div>
          </div>

          <CaseStudyTabs items={caseStudies} />

          <div className="flex justify-center pt-10 md:pt-12">
            <div className="w-full md:w-1/3 md:px-8">
              <Button
                href="/case-studies"
                colour="royal"
                appearance="outline"
                block
              >
                Read all case studies &#8594;
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page } =
    (await fetchPage(
      gql`
        query forBusinessesPageQuery {
          page(where: { slug: "for-businesses" }) {
            ...pageData # Fragment is inside the method
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  return {
    props: {
      preview,
      page,
    },
    revalidate: 3600,
  };
};
